import { ReactNode, useCallback, useState } from 'react';

import { Modal, TButtonColor, TButtonProps } from '@treezor/design-system';
import { useTranslation } from 'react-i18next';

interface IConfirmationDialogProps {
  children?: ReactNode;
}

export interface IConfirmationDialogReturn {
  ConfirmationDialog: (props: IConfirmationDialogProps) => React.JSX.Element;
  toggle: () => void;
}

interface IConfirmButtonProps extends TButtonProps {
  label: string;
  color?: TButtonColor;
  action?: () => void;
}

export interface IConfirmationDialogHook {
  id?: string;
  title?: string;
  highlight?: string;
  description?: string;
  confirm?: IConfirmButtonProps;
  cancel?: {
    label: string;
  };
}

export function useConfirmationDialog({
  id,
  title,
  highlight,
  description,
  confirm,
  cancel,
}: IConfirmationDialogHook): IConfirmationDialogReturn {
  const [isOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const { label: confirmLabel, color: confirmColor, action: confirmAction, ...rest } = confirm ?? {};

  const handleConfirm = useCallback(() => {
    confirmAction?.();
    setIsModalOpen(false);
  }, [confirmAction]);

  const ConfirmationDialog = ({ children }: { children?: ReactNode }) => (
    <Modal
      data-testid={id}
      isOpen={isOpen}
      onClose={() => setIsModalOpen(false)}
      title={title}
      actions={
        !!children
          ? undefined
          : [
              {
                variant: 'tertiary',
                children: cancel?.label || t('pages.cores.cancel'),
                onClick: () => setIsModalOpen(false),
              },
              {
                variant: 'primary',
                children: confirmLabel,
                onClick: handleConfirm,
                color: confirmColor,
                ...rest,
              },
            ]
      }
    >
      {children ? (
        <>{children}</>
      ) : (
        <>
          {highlight && <p className="typo-body-medium-paragraph">{highlight}</p>}
          {description && <p className="typo-body-regular-paragraph">{description}</p>}
        </>
      )}
    </Modal>
  );

  return {
    ConfirmationDialog,
    toggle: () => setIsModalOpen((open) => !open),
  };
}
